/**
 * Sprint TS-B — HM Admin TalentStart Workspace (read-only).
 *
 * Founder directive: minimum admin workspace. Reuse existing data only.
 * No charts, no analytics, no new workflows. Mirrors the TalentTrain TT-C
 * AdminTalentTrainWorkspace pattern.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import { Loader2, FileText, Download } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

function StatBox({ label, value, testid }) {
  return (
    <Card className="p-4 shadow-card" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <p className="text-2xl font-bold tabular-nums mt-1">{value}</p>
    </Card>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed: 'bg-success/15 text-success border-success/30',
    in_progress: 'bg-info/15 text-info border-info/30',
    overdue: 'bg-destructive/15 text-destructive border-destructive/30',
    not_started: 'bg-muted text-muted-foreground border-border',
    pending: 'bg-muted text-muted-foreground border-border',
  };
  return (
    <Badge className={`text-xs ${map[status] || 'bg-muted text-muted-foreground border-border'}`}>
      {status || '—'}
    </Badge>
  );
}

export default function AdminTalentStartWorkspace({ companyId }) {
  const { t } = useTranslation();
  const [overview, setOverview] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [ov, tpls, asgs, reps] = await Promise.all([
        adminApi.tsAdminOverview(companyId),
        adminApi.tsAdminListTemplates(companyId),
        adminApi.tsAdminListAssignments(companyId),
        adminApi.tsAdminListReports(companyId),
      ]);
      setOverview(ov);
      setTemplates(tpls?.items || []);
      setAssignments(asgs?.items || []);
      setReports(reps?.items || []);
    } catch (e) {
      toast.error(t('toasts.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [companyId, t]);

  useEffect(() => { load(); }, [load]);

  if (loading || !overview) {
    return (
      <div className="py-10 text-center text-muted-foreground" data-testid="adm-ts-loading">
        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="adm-ts-workspace">
      {/* KPI strip — 6 cards per founder spec */}
      <div className="grid grid-cols-2 sm:grid-cols-3 2xl:grid-cols-6 gap-3">
        <StatBox label={t('adminTalentStart.kpi.active', { defaultValue: 'Active' })}
                 value={overview.active} testid="adm-ts-kpi-active" />
        <StatBox label={t('adminTalentStart.kpi.completed', { defaultValue: 'Completed' })}
                 value={overview.completed} testid="adm-ts-kpi-completed" />
        <StatBox label={t('adminTalentStart.kpi.pending', { defaultValue: 'Pending' })}
                 value={overview.pending} testid="adm-ts-kpi-pending" />
        <StatBox label={t('adminTalentStart.kpi.avg', { defaultValue: 'Avg completion' })}
                 value={`${overview.average_completion_pct ?? 0}%`} testid="adm-ts-kpi-avg" />
        <StatBox label={t('adminTalentStart.kpi.templates', { defaultValue: 'Templates' })}
                 value={overview.templates_total} testid="adm-ts-kpi-templates" />
        <StatBox label={t('adminTalentStart.kpi.employees', { defaultValue: 'Employees onboarding' })}
                 value={overview.employees_onboarding} testid="adm-ts-kpi-employees" />
      </div>

      <Tabs defaultValue="journeys" className="w-full">
        <TabsList data-testid="adm-ts-tabs">
          <TabsTrigger value="journeys" data-testid="adm-ts-tab-journeys">
            {t('adminTalentStart.tabs.journeys', { defaultValue: 'Onboarding journeys' })} ({assignments.length})
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="adm-ts-tab-templates">
            {t('adminTalentStart.tabs.templates', { defaultValue: 'Templates' })} ({templates.length})
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="adm-ts-tab-reports">
            {t('adminTalentStart.tabs.reports', { defaultValue: 'Reports' })} ({reports.length})
          </TabsTrigger>
        </TabsList>

        {/* Onboarding journeys (= assignments) */}
        <TabsContent value="journeys" className="mt-4" data-testid="adm-ts-panel-journeys">
          <Card className="p-5 shadow-card">
            {assignments.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {t('adminTalentStart.journeys.empty', { defaultValue: 'No onboarding journeys yet for this company.' })}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.employee', { defaultValue: 'Employee' })}</TableHead>
                    <TableHead>{t('common.template', { defaultValue: 'Template' })}</TableHead>
                    <TableHead>{t('common.status', { defaultValue: 'Status' })}</TableHead>
                    <TableHead className="text-center">{t('common.progress', { defaultValue: 'Progress' })}</TableHead>
                    <TableHead className="text-center">{t('common.hours', { defaultValue: 'Hours' })}</TableHead>
                    <TableHead>{t('common.due', { defaultValue: 'Due' })}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignments.map((a, i) => (
                    <TableRow key={a.id} data-testid={`adm-ts-journey-row-${i}`}>
                      <TableCell className="font-medium">{a.user_name || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{a.template_id?.slice(0, 8) || '—'}…</TableCell>
                      <TableCell><StatusBadge status={a.status} /></TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary"
                                 style={{ width: `${a.progress_pct || 0}%` }} />
                          </div>
                          <span className="text-xs tabular-nums">{a.progress_pct || 0}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-sm tabular-nums">
                        {a.hours_done || 0} / {a.hours_estimated || 0}
                      </TableCell>
                      <TableCell className="text-sm">
                        {(a.due_at || '').toString().slice(0, 10) || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Templates */}
        <TabsContent value="templates" className="mt-4" data-testid="adm-ts-panel-templates">
          <Card className="p-5 shadow-card">
            {templates.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {t('adminTalentStart.templates.empty', { defaultValue: 'No templates yet.' })}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.name', { defaultValue: 'Name' })}</TableHead>
                    <TableHead>{t('common.position', { defaultValue: 'Position' })}</TableHead>
                    <TableHead className="text-center">{t('common.stages', { defaultValue: 'Stages' })}</TableHead>
                    <TableHead>{t('common.status', { defaultValue: 'Status' })}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((tpl, i) => (
                    <TableRow key={tpl.id} data-testid={`adm-ts-template-row-${i}`}>
                      <TableCell className="font-medium">{tpl.name || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{tpl.position || '—'}</TableCell>
                      <TableCell className="text-center text-sm tabular-nums">
                        {(tpl.stages || []).length}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{tpl.status || 'active'}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Reports */}
        <TabsContent value="reports" className="mt-4" data-testid="adm-ts-panel-reports">
          <Card className="p-5 shadow-card">
            {reports.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {t('adminTalentStart.reports.empty', { defaultValue: 'No TalentStart reports yet for this company.' })}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.kind', { defaultValue: 'Kind' })}</TableHead>
                    <TableHead>{t('common.filename', { defaultValue: 'File' })}</TableHead>
                    <TableHead>{t('common.released', { defaultValue: 'Released' })}</TableHead>
                    <TableHead>{t('common.status', { defaultValue: 'Status' })}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((r, i) => (
                    <TableRow key={r.id} data-testid={`adm-ts-report-row-${i}`}>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                          {r.kind || 'report'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {r.filename || '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {(r.released_at || '').slice(0, 19).replace('T', ' ') || '—'}
                      </TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
